import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';

/**
 * Get all teacher notes with optional filters
 * @param {Object} filters - Optional filters (division_id, subject_id, chapter_id, search)
 * @returns {Promise} API response
 */
export const getTeacherNotes = async (filters = {}) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const params = new URLSearchParams();
  if (filters.division_id) params.append('division_id', filters.division_id);
  if (filters.subject_id) params.append('subject_id', filters.subject_id);
  if (filters.chapter_id) params.append('chapter_id', filters.chapter_id);
  if (filters.search) params.append('search', filters.search);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = apiUrl(`v1/teachers/notes/${queryString}`);

  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  return response.data;
};

/**
 * Get teacher notes for a student (filtered by student's division and chapter).
 * @param {number} chapterId - The ID of the chapter to fetch notes for.
 * @returns {Promise} API response with list of notes.
 */
export const getStudentTeacherNotes = async (chapterId) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl('users/teacher-notes/'),
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { chapter_id: chapterId }
    }
  );

  return response.data.notes;
};

/**
 * Create a manual note (without file upload)
 * @param {Object} data - Note data (division_id, subject_id, chapter_id, title, content, visible_to_students, allow_download)
 * @returns {Promise} API response
 */
export const createTeacherNote = async (data) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.post(
    apiUrl('v1/teachers/notes/create/'),
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * Update a teacher note
 * @param {number} id - Note ID
 * @param {Object} data - Updated note data (title, content, visible_to_students, allow_download)
 * @returns {Promise} API response
 */
export const updateTeacherNote = async (id, data) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.put(
    apiUrl(`v1/teachers/notes/${id}/`),
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

/**
 * Delete a teacher note
 * @param {number} id - Note ID
 * @returns {Promise} API response
 */
export const deleteTeacherNote = async (id) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.delete(
    apiUrl(`v1/teachers/notes/${id}/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
};

/**
 * Upload a file for OCR and AI correction processing
 * @param {File} file - File to upload (PDF or image)
 * @param {number[]} divisionIds - Array of division/class IDs (can be empty - will be assigned later)
 * @param {number} chapterId - Chapter ID
 * @param {string} title - Optional title for the note
 * @param {boolean} visibleToStudents - Whether note is visible to students
 * @param {boolean} allowDownload - Whether students can download
 * @returns {Promise} API response with note data
 */
export const uploadNoteFile = async (file, divisionIds = [], chapterId, title = '', visibleToStudents = true, allowDownload = true) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const formData = new FormData();
  formData.append('file', file);
  // Append division_ids as array (can be empty)
  if (divisionIds.length > 0) {
    divisionIds.forEach(id => formData.append('division_ids', id));
  }
  formData.append('chapter_id', chapterId);
  if (title) formData.append('title', title);
  formData.append('visible_to_students', visibleToStudents);
  formData.append('allow_download', allowDownload);

  const response = await axios.post(
    apiUrl('v1/teachers/notes/upload/'),
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data;
};

/**
 * Get processing status of a note (for polling)
 * @param {number} id - Note ID
 * @returns {Promise} API response with status
 */
export const getNoteStatus = async (id) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl(`v1/teachers/notes/${id}/status/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
};

/**
 * Get full note details including OCR and corrected text
 * @param {number} id - Note ID
 * @returns {Promise} API response with full note data
 */
export const getNoteDetail = async (id) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl(`v1/teachers/notes/${id}/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
};

/**
 * Get download URL for note file
 * @param {number} id - Note ID
 * @returns {Promise} API response with download URL
 */
export const downloadNoteFile = async (id) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl(`v1/teachers/notes/${id}/download/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
};

/**
 * Repair math / LaTeX spans in a teacher note.
 * @param {number} id - Note ID
 * @param {Object} payload - Optional payload (e.g. { snippets: [{ latex, display }] })
 * @returns {Promise} Updated note data
 */
export const repairTeacherNoteMath = async (id, payload = {}) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.post(
    apiUrl(`v1/teachers/notes/${id}/repair-math/`),
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

